import { trace } from "@opentelemetry/api";

import {
  INTENTIONAL_CLOSE_CODE,
  connectSingleUseISocket,
  serverWsPath,
} from "@superblocksteam/shared";
import type {
  Agent,
  ClientMethods,
  ISocketClient,
  Organization,
  ServerMethods,
} from "@superblocksteam/shared";

import AuthProvider from "auth/auth0";
import { SUPERBLOCKS_UI_SOCKET_ENDPOINT_BASE_URL } from "env";

import { createRequestHandlers } from "./handlers";

const baseURL = SUPERBLOCKS_UI_SOCKET_ENDPOINT_BASE_URL || "/api/";
const wsPath = `${baseURL}${serverWsPath}`;
const wsPathParsed = new URL(wsPath, window.location.href);
wsPathParsed.protocol = wsPathParsed.protocol === "https:" ? "wss:" : "ws:";
const proxyPort = process.env.UI_CODE_MODE_PROXY_PORT || "3000";
const serverPort = process.env.SUPERBLOCKS_SERVER_PORT || "8080";
if (wsPathParsed.origin === `ws://localhost:${proxyPort}`) {
  wsPathParsed.port = serverPort;
}
const wsUrl = wsPathParsed.href;

export type StdISocketRPCClient = ISocketClient<ServerMethods>;

export async function makeSingleUseSocketConnection(
  agents: Agent[],
  organization: Organization,
): Promise<StdISocketRPCClient> {
  if (!AuthProvider.ready()) {
    throw new Error("AuthProvider not ready");
  }
  const token = await AuthProvider.generateToken();
  const requestHandlers = createRequestHandlers(agents, organization);
  const authorization = `Bearer ${token}`;
  return await connectSingleUseISocket<ServerMethods, ClientMethods>(
    wsUrl,
    authorization,
    requestHandlers,
    [],
    trace.getTracer("superblocks-ui"),
    {
      onClose: (event) => {
        if (event.code !== INTENTIONAL_CLOSE_CODE) {
          // This would happen if a signing-enabled org has some unexpected failure
          // during the signing process - but it wasn't handled before and is not
          // handled here
          console.error("Socket closed unexpectedly", event);
        }
      },
      timeouts: {
        connectionTimeoutInSeconds: 6 * 60, // 6 minutes
        noResponseTimeoutInSeconds: 5 * 60, // 5 minutes
      },
    },
  );
}
